
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { CheckCircle2, AlertTriangle, Wallet, RefreshCw, Info, TrendingUp, FileText, Scale, Activity, Banknote, PieChart } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

const TXN_LABEL = {
  advance_disbursed: 'Advance Disbursed',
  claim_submitted: 'Claim Submitted',
  claim_approved: 'Claim Approved',
  claim_rejected: 'Claim Rejected',
  claim_adjustment: 'Adjusted Against Advance',
  salary_recovery: 'Recovered via Salary',
  employee_refund: 'Refund to Company',
  company_reimbursement: 'Company Paid You',
  write_off: 'Written Off',
  carry_forward: 'Carried to Next Month',
  close: 'Advance Closed',
  advance_payment: 'Finance Advance Payment',
  reimbursement_payment: 'Finance Reimbursement Payment',
  recovery_collection: 'Recovery Collection',
  finance_payment: 'Finance Payment',
};

// Retry helper function
const fetchWithRetry = async (url, options, retries = 3, delay = 1000) => {
  let lastError;
  for (let i = 0; i < retries; i++) {
    try {
      const response = await axios.get(url, options);
      return response;
    } catch (error) {
      lastError = error;
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1))); // Exponential backoff
      }
    }
  }
  throw lastError;
};

const LedgerTimeline = ({ adv }) => {
  const steps = [
    { label: 'Advance Approved', done: true },
    { label: 'Finance Paid', done: adv.finance_paid || adv.finance_paid_amount > 0 },
    { label: 'Claim Submitted', done: adv.claim_submitted_amount > 0 || adv.claim_approved_amount > 0 },
    { label: 'Claim Approved', done: adv.claim_approved_amount > 0 },
    { label: 'Settlement Done', done: adv.settlement_status === 'Fully Settled' || adv.closed },
    { label: 'Closed', done: adv.closed }
  ];

  return (
    <div className="flex items-center gap-1.5 py-3 overflow-x-auto text-[10px] sm:text-xs">
      {steps.map((s, idx) => (
        <React.Fragment key={idx}>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
              s.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {s.done ? '✓' : idx + 1}
            </span>
            <span className={s.done ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
              {s.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <span className={`h-[2px] w-6 shrink-0 ${
              steps[idx + 1].done ? 'bg-emerald-400' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function ESSAdvanceLedger() {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAdvance, setExpandedAdvance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reimbursements, setReimbursements] = useState([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, l, r] = await Promise.all([
        fetchWithRetry(`${API_URL}/advance-ledger/me/summary`, { withCredentials: true }),
        fetchWithRetry(`${API_URL}/advance-ledger/me/ledger`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/reimbursements`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSummary(s.data || {
        total_approved_advances: 0,
        total_finance_paid: 0,
        total_claim_submitted: 0,
        total_claim_approved: 0,
        total_claim_rejected: 0,
        total_refund_by_employee: 0,
        total_recovered_via_salary: 0,
        total_paid_by_company: 0,
        current_outstanding_advance: 0,
        total_closed_advances: 0,
        total_open_advances: 0,
        total_advance: 0,
        total_pending_claim: 0,
        per_advance: [],
        claims: []
      });
      setLedger(l.data || { rows: [] });
      setReimbursements(r.data || []);
    } catch (e) {
      setError(e);
      if (!e.response) {
        toast.error('Unable to connect to server. Please try again.');
      } else {
        // Don't show error toast if it's just empty data
        if (!(e.response?.data?.success === false)) {
          toast.error('Failed to load ledger');
        }
      }
      // Set default empty data even on error
      setSummary({
        total_approved_advances: 0,
        total_finance_paid: 0,
        total_claim_submitted: 0,
        total_claim_approved: 0,
        total_claim_rejected: 0,
        total_refund_by_employee: 0,
        total_recovered_via_salary: 0,
        total_paid_by_company: 0,
        current_outstanding_advance: 0,
        total_closed_advances: 0,
        total_open_advances: 0,
        total_advance: 0,
        total_pending_claim: 0,
        per_advance: [],
        claims: []
      });
      setLedger({ rows: [] });
      setReimbursements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const getStatusBadge = (status) => {
    const s = String(status || '').trim();
    const formatted = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    const variants = {
      'Paid': 'bg-emerald-100 text-emerald-700',
      'Approved': 'bg-teal-100 text-teal-800',
      'Processed': 'bg-blue-100 text-blue-700',
      'Settled': 'bg-emerald-100 text-emerald-700',
      'Pending': 'bg-amber-100 text-amber-700',
      'Rejected': 'bg-rose-100 text-rose-700',
      'Unpaid': 'bg-amber-100 text-amber-700',
      'Fully Settled': 'bg-emerald-100 text-emerald-700',
      'Partially Settled': 'bg-amber-100 text-amber-700',
      'Open': 'bg-blue-100 text-blue-700',
      'Closed': 'bg-gray-100 text-gray-700',
    };
    return (
      <Badge className={variants[formatted] || variants[s] || 'bg-gray-100 text-gray-700'}>
        {formatted}
      </Badge>
    );
  };

  const toggleExpand = (advanceId) => {
    setExpandedAdvance(expandedAdvance === advanceId ? null : advanceId);
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Advance & Claim Ledger</h1>
            <p className="text-sm text-slate-500 mt-1">
              Complete view of your advances, claims, settlements, and payments
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 rounded-lg px-4 py-2 text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {loading && (
          <div className="space-y-6">
            {/* Skeleton Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="shadow-sm">
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-32" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="shadow-sm">
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-32" />
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Skeleton Tabs */}
            <Skeleton className="h-10 w-full mb-6" />
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-6 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {!loading && summary && (
          <>
            {summary.per_advance?.length === 0 && summary.claims?.length === 0 && ledger?.rows?.length === 0 && (
              <div className="text-center py-16 bg-slate-50 rounded-lg mb-6">
                <Info className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-800 mb-2">No advance or claim records found.</h3>
                <p className="text-slate-500">You haven't created any advances or claims yet.</p>
              </div>
            )}
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* 1. Active Advances Card */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-sm font-medium text-slate-600">Active Advances</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    {summary.total_open_advances || 0} Open
                  </div>
                  <div className="space-y-1 mb-3">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Total Requested</span>
                      <span className="font-medium text-slate-700">{fmtINR(summary.total_advance)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Approved</span>
                      <span className="font-medium text-slate-700">{fmtINR(summary.total_advance)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Finance Paid</span>
                      <span className="font-medium text-emerald-700">{fmtINR(summary.total_finance_paid)}</span>
                    </div>
                  </div>
                  {/* Advance Utilization Progress */}
                  {(() => {
                    const totalSettled = (summary.total_claim_approved || 0) + 
                                         (summary.total_refund_by_employee || 0) + 
                                         (summary.total_recovered_via_salary || 0);
                    const utilization = summary.total_advance > 0 
                      ? Math.min(100, (totalSettled / summary.total_advance) * 100) 
                      : 0;
                    return (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Advance Utilization</span>
                          <span className="font-medium">{Math.round(utilization)}%</span>
                        </div>
                        <Progress value={utilization} className="h-2" />
                      </div>
                    );
                  })()}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between text-xs">
                    <span className="text-slate-500">Open: {summary.total_open_advances || 0}</span>
                    <span className="text-slate-500">Closed: {summary.total_closed_advances || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* 2. Claims Summary Card */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    <CardTitle className="text-sm font-medium text-slate-600">Claims Summary</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">Submitted</div>
                      <div className="text-lg font-bold text-slate-700">{fmtINR(summary.total_claim_submitted)}</div>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <div className="text-xs text-emerald-600">Approved</div>
                      <div className="text-lg font-bold text-emerald-700">{fmtINR(summary.total_claim_approved)}</div>
                    </div>
                    <div className="p-2 bg-red-50 rounded-lg">
                      <div className="text-xs text-red-600">Rejected</div>
                      <div className="text-lg font-bold text-red-700">{fmtINR(summary.total_claim_rejected)}</div>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <div className="text-xs text-amber-600">Pending</div>
                      <div className="text-lg font-bold text-amber-700">{fmtINR(summary.total_pending_claim)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 3. Settlement Summary Card */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Scale className="h-5 w-5 text-teal-600" />
                    <CardTitle className="text-sm font-medium text-slate-600">Settlement Summary</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">Adjusted</div>
                      <div className="text-lg font-bold text-slate-700">{fmtINR(summary.total_claim_approved)}</div>
                    </div>
                    <div className="p-2 bg-orange-50 rounded-lg">
                      <div className="text-xs text-orange-600">Refund</div>
                      <div className="text-lg font-bold text-orange-700">{fmtINR(summary.total_refund_by_employee)}</div>
                    </div>
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <div className="text-xs text-blue-600">Company Payable</div>
                      <div className="text-lg font-bold text-blue-700">{fmtINR(summary.total_paid_by_company)}</div>
                    </div>
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <div className="text-xs text-cyan-600">Recovery</div>
                      <div className="text-lg font-bold text-cyan-700">{fmtINR(summary.total_recovered_via_salary)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* 4. Outstanding Position Card */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-violet-600" />
                    <CardTitle className="text-sm font-medium text-slate-600">Outstanding Position</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    let label, amount, colorClass;
                    if (summary.current_outstanding_advance > 0.01) {
                      label = "Employee to Refund";
                      amount = summary.current_outstanding_advance;
                      colorClass = "text-red-700 bg-red-50";
                    } else if (summary.current_outstanding_advance < -0.01) {
                      label = "Company to Pay";
                      amount = Math.abs(summary.current_outstanding_advance);
                      colorClass = "text-blue-700 bg-blue-50";
                    } else {
                      label = "Fully Settled";
                      amount = 0;
                      colorClass = "text-emerald-700 bg-emerald-50";
                    }
                    return (
                      <div className={`p-3 rounded-lg ${colorClass}`}>
                        <div className="text-xs mb-1">{label}</div>
                        <div className="text-2xl font-bold">{fmtINR(amount)}</div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* 5. Finance Payment Status Card */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                    <CardTitle className="text-sm font-medium text-slate-600">Finance Payment Status</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <div className="text-xs text-emerald-600">Paid Advances</div>
                      <div className="text-lg font-bold text-emerald-700">
                        {(summary.per_advance || []).filter(a => a.payment_status === "Fully Paid").length}
                      </div>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <div className="text-xs text-amber-600">Pending Payment</div>
                      <div className="text-lg font-bold text-amber-700">
                        {(summary.per_advance || []).filter(a => a.payment_status === "Pending Payment" || a.payment_status === "Partially Paid").length}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                    Last Paid: {(() => {
                      const paidAdvances = (summary.per_advance || [])
                        .filter(a => a.finance_payment_date)
                        .sort((a, b) => new Date(b.finance_payment_date) - new Date(a.finance_payment_date));
                      return paidAdvances.length > 0 ? formatDate(paidAdvances[0].finance_payment_date) : "-";
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* 6. Advance Health Card */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-slate-600" />
                    <CardTitle className="text-sm font-medium text-slate-600">Advance Health</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <div className="text-xs text-blue-600">Open</div>
                      <div className="text-lg font-bold text-blue-700">{summary.total_open_advances || 0}</div>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <div className="text-xs text-purple-600">In Claim</div>
                      <div className="text-lg font-bold text-purple-700">
                        {(summary.per_advance || []).filter(a => a.claim_submitted_amount > 0 && a.claim_submitted_amount > a.claim_approved_amount).length}
                      </div>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <div className="text-xs text-amber-600">Settlement Pending</div>
                      <div className="text-lg font-bold text-amber-700">
                        {(summary.per_advance || []).filter(a => a.settlement_status === "Partially Settled" || a.settlement_status === "Open").length}
                      </div>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <div className="text-xs text-emerald-600">Closed</div>
                      <div className="text-lg font-bold text-emerald-700">{summary.total_closed_advances || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="advances" className="w-full">
              <TabsList className="grid grid-cols-5 mb-6">
                <TabsTrigger value="advances">My Advances</TabsTrigger>
                <TabsTrigger value="claims">Claim History</TabsTrigger>
                <TabsTrigger value="reimbursements">Reimbursement History</TabsTrigger>
                <TabsTrigger value="settlements">Settlement History</TabsTrigger>
                <TabsTrigger value="ledger">Advance Ledger</TabsTrigger>
              </TabsList>

              {/* My Advances */}
              <TabsContent value="advances">
                <Card>
                  <CardHeader>
                    <CardTitle>My Advances</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Advance Ref No</TableHead>
                            <TableHead>Request Date</TableHead>
                            <TableHead>Purpose</TableHead>
                            <TableHead className="text-right">Requested Amount</TableHead>
                            <TableHead className="text-right">Approved Amount</TableHead>
                            <TableHead className="text-right">Finance Paid Amount</TableHead>
                            <TableHead>Finance Payment Date</TableHead>
                            <TableHead className="text-right">Claim Submitted Amount</TableHead>
                            <TableHead className="text-right">Claim Approved Amount</TableHead>
                            <TableHead>Claim Date</TableHead>
                            <TableHead className="text-right">Employee Refund Amount</TableHead>
                            <TableHead className="text-right">Salary Recovery Amount</TableHead>
                            <TableHead className="text-right">Company Payable Amount</TableHead>
                            <TableHead className="text-right">Pending Balance</TableHead>
                            <TableHead className="text-right">Current Outstanding</TableHead>
                            <TableHead>Payment Status</TableHead>
                            <TableHead>Settlement Status</TableHead>
                            <TableHead>Last Updated Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(summary.per_advance || []).map((adv, i) => (
                            <TableRow key={adv.advance_id}>
                              <TableCell className="font-mono font-medium">{adv.finance_ref}</TableCell>
                              <TableCell>{formatDate(adv.request_date)}</TableCell>
                              <TableCell className="max-w-xs truncate">{adv.purpose}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.requested_amount)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.approved_amount)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.finance_paid_amount)}</TableCell>
                              <TableCell>{formatDate(adv.finance_payment_date)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.claim_submitted_amount)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.claim_approved_amount)}</TableCell>
                              <TableCell>{formatDate(adv.claim_date)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.employee_refund_amount)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.salary_recovery_amount)}</TableCell>
                              <TableCell className="text-right">{fmtINR(adv.company_payable_amount)}</TableCell>
                              <TableCell className={`text-right font-medium ${
                                adv.pending_balance > 0 ? 'text-amber-700' : adv.pending_balance < 0 ? 'text-blue-700' : 'text-emerald-700'
                              }`}>
                                {fmtINR(Math.abs(adv.pending_balance))}
                              </TableCell>
                              <TableCell className={`text-right font-medium ${
                                adv.current_outstanding > 0 ? 'text-amber-700' : adv.current_outstanding < 0 ? 'text-blue-700' : 'text-emerald-700'
                              }`}>
                                {fmtINR(Math.abs(adv.current_outstanding))}
                              </TableCell>
                              <TableCell>{getStatusBadge(adv.payment_status)}</TableCell>
                              <TableCell>{getStatusBadge(adv.settlement_status)}</TableCell>
                              <TableCell>{formatDate(adv.last_updated_date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Claim History */}
              <TabsContent value="claims">
                <Card>
                  <CardHeader>
                    <CardTitle>Claim History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reference</TableHead>
                          <TableHead>Linked Advance</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Claim Amount</TableHead>
                          <TableHead className="text-right">Approved Amount</TableHead>
                          <TableHead className="text-right">Rejected Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(summary.claims || []).map((c, j) => {
                          const linkedAdvance = (summary.per_advance || []).find(a => a.advance_id === c.advance_id);
                          return (
                            <TableRow key={j}>
                              <TableCell className="font-mono">{c.sheet_number || String(c._id).slice(-6)}</TableCell>
                              <TableCell className="font-mono">{linkedAdvance?.finance_ref || '-'}</TableCell>
                              <TableCell>{formatDate(c.created_at || c.submitted_at)}</TableCell>
                              <TableCell className="text-right">{fmtINR(c.total_amount)}</TableCell>
                              <TableCell className="text-right">
                                {c.status === 'approved' || c.status === 'paid' || c.status === 'settled' ? fmtINR(c.total_amount) : '₹0.00'}
                              </TableCell>
                              <TableCell className="text-right">
                                {c.status === 'rejected' ? fmtINR(c.total_amount) : '₹0.00'}
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(c.status || 'Submitted')}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Reimbursement History */}
              <TabsContent value="reimbursements">
                <Card>
                  <CardHeader>
                    <CardTitle>Reimbursement History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reference / Claim No.</TableHead>
                          <TableHead>Linked Advance</TableHead>
                          <TableHead>Payment Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Payment Mode</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const history = (reimbursements || [])
                            .filter(r => ['approved', 'paid', 'processed', 'settled'].includes(r.status))
                            .sort((a, b) => new Date(b.created_at || b.bill_date || 0) - new Date(a.created_at || a.bill_date || 0));

                          if (history.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={6} className="text-center py-6 text-slate-500 italic">
                                  No reimbursement history found.
                                </TableCell>
                              </TableRow>
                            );
                          }

                          return history.map((r, k) => {
                            const linkedAdvance = r.advance_id ? (summary?.per_advance || []).find(a => a.advance_id === r.advance_id) : null;
                            return (
                              <TableRow key={k}>
                                <TableCell className="font-mono">{r.bill_number || '—'}</TableCell>
                                <TableCell className="font-mono">{linkedAdvance?.finance_ref || r.advance_id || '-'}</TableCell>
                                <TableCell>{formatDate(r.payment_date || r.paid_at || r.created_at || r.bill_date)}</TableCell>
                                <TableCell className="text-right">{fmtINR(r.amount || 0)}</TableCell>
                                <TableCell>{r.payment_mode || '—'}</TableCell>
                                <TableCell>{getStatusBadge(r.status)}</TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Settlement History */}
              <TabsContent value="settlements">
                <Card>
                  <CardHeader>
                    <CardTitle>Settlement History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Advance Reference</TableHead>
                          <TableHead>Settlement Date</TableHead>
                          <TableHead className="text-right">Claim Adjustment</TableHead>
                          <TableHead className="text-right">Refund</TableHead>
                          <TableHead className="text-right">Recovery</TableHead>
                          <TableHead className="text-right">Company Payable</TableHead>
                          <TableHead>Settlement Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(summary.per_advance || []).map((adv, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono font-medium">{adv.finance_ref}</TableCell>
                            <TableCell>{formatDate(adv.disbursement_date)}</TableCell>
                            <TableCell className="text-right">{fmtINR(adv.claim_approved_amount)}</TableCell>
                            <TableCell className="text-right">{fmtINR(adv.employee_refund_amount)}</TableCell>
                            <TableCell className="text-right">{fmtINR(adv.salary_recovery_amount)}</TableCell>
                            <TableCell className="text-right">{fmtINR(adv.company_payable_amount)}</TableCell>
                            <TableCell>{getStatusBadge(adv.settlement_status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Advance Ledger */}
              <TabsContent value="ledger">
                <Card>
                  <CardHeader>
                    <CardTitle>Employee Advance Ledger</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Search and Filters */}
                    <div className="flex flex-wrap items-center gap-3 mb-6 bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div className="flex-1 min-w-[200px]">
                        <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Search Reference / Purpose</label>
                        <input 
                          type="text"
                          placeholder="Search ADV / RBM ref..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                        />
                      </div>
                      
                      <div className="w-[180px]">
                        <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Status</label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full text-xs px-2 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                        >
                          <option value="ALL">All Statuses</option>
                          <option value="Open">Open</option>
                          <option value="Closed">Closed</option>
                          <option value="Fully Settled">Fully Settled</option>
                          <option value="Partially Settled">Partially Settled</option>
                          <option value="In Claim">In Claim</option>
                        </select>
                      </div>

                      <div className="w-[140px]">
                        <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">From Date</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                        />
                      </div>

                      <div className="w-[140px]">
                        <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">To Date</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                        />
                      </div>

                      {(searchTerm || statusFilter !== 'ALL' || startDate || endDate) && (
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('ALL');
                            setStartDate('');
                            setEndDate('');
                          }}
                          className="self-end px-3 py-2 text-xs bg-slate-200 text-slate-700 hover:bg-slate-300 rounded-md transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {(() => {
                      const filteredAdvances = (summary?.per_advance || []).filter(adv => {
                        const matchesSearch = () => {
                          if (!searchTerm) return true;
                          const term = searchTerm.toLowerCase();
                          if (adv.finance_ref?.toLowerCase().includes(term)) return true;
                          if (adv.loan_number?.toLowerCase().includes(term)) return true;
                          if (adv.purpose?.toLowerCase().includes(term)) return true;
                          
                          const linkedClaims = (summary.claims || []).filter(c => c.advance_id === adv.advance_id);
                          if (linkedClaims.some(c => c.sheet_number?.toLowerCase().includes(term))) return true;
                          return false;
                        };

                        const matchesStatus = () => {
                          if (statusFilter === 'ALL') return true;
                          return adv.settlement_status === statusFilter || adv.payment_status === statusFilter || (adv.closed && statusFilter === 'Closed');
                        };

                        const matchesDate = () => {
                          if (!adv.disbursement_date) return true;
                          const disbDate = adv.disbursement_date.slice(0, 10);
                          if (startDate && disbDate < startDate) return false;
                          if (endDate && disbDate > endDate) return false;
                          return true;
                        };

                        return matchesSearch() && matchesStatus() && matchesDate();
                      });

                      if (filteredAdvances.length === 0) {
                        return (
                          <div className="text-center py-16 bg-slate-50 rounded-lg">
                            <Info className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-slate-800 mb-2">No Ledger Records Found</h3>
                            <p className="text-slate-500">No advances match your search filters.</p>
                          </div>
                        );
                      }

                      return filteredAdvances.map((adv) => {
                        const isExpanded = expandedAdvance === adv.advance_id;
                        const linkedClaims = (summary.claims || []).filter(c => c.advance_id === adv.advance_id);
                        
                        // Get all ledger rows for this advance
                        const localRows = (ledger?.rows || [])
                          .filter(r => r.loan_id === adv.advance_id)
                          .sort((a, b) => new Date(a.txn_date) - new Date(b.txn_date));

                        // Compute local running balance
                        let runningBal = 0;
                        const processedRows = localRows.map(r => {
                          runningBal += (r.debit || 0) - (r.credit || 0);
                          return { ...r, running_balance_local: runningBal };
                        });

                        return (
                          <div key={adv.advance_id} className="border border-slate-200 rounded-lg mb-4 overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                            {/* Parent Header Row */}
                            <div 
                              className="flex flex-wrap items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors gap-4"
                              onClick={() => setExpandedAdvance(isExpanded ? null : adv.advance_id)}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-slate-400 text-sm transition-transform duration-200">
                                  {isExpanded ? '▼' : '▶'}
                                </span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold text-slate-800 text-sm">{adv.finance_ref}</span>
                                    <Badge variant="outline" className={`text-[10px] font-semibold ${
                                      adv.closed ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-700 border-amber-200'
                                    }`}>
                                      {adv.settlement_status}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-slate-500 mt-0.5">
                                    Purpose: <span className="font-medium text-slate-700">{adv.purpose}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-6 text-xs text-slate-655">
                                <div>
                                  <div className="text-slate-400">Date</div>
                                  <div className="font-medium mt-0.5">{formatDate(adv.disbursement_date)}</div>
                                </div>
                                <div>
                                  <div className="text-slate-400">Approved Amount</div>
                                  <div className="font-bold text-slate-800 mt-0.5">{fmtINR(adv.approved_amount)}</div>
                                </div>
                                <div>
                                  <div className="text-slate-400">Outstanding</div>
                                  <div className={`font-bold mt-0.5 ${adv.current_outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                                    {fmtINR(adv.current_outstanding)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Child Transactions */}
                            {isExpanded && (
                              <div className="p-4 border-t border-slate-100 bg-white">
                                {/* Timeline flow */}
                                <div className="mb-6 pb-4 border-b border-slate-100">
                                  <h4 className="text-[11px] font-bold uppercase text-slate-400 tracking-wider mb-2">Transaction Timeline</h4>
                                  <LedgerTimeline adv={adv} />
                                </div>

                                {/* Details sections */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                  {/* Finance Payment details */}
                                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-150">
                                    <h5 className="text-xs font-bold text-slate-600 mb-2 border-b pb-1 border-slate-200">Finance Disbursement Payment</h5>
                                    {adv.finance_paid_amount > 0 ? (
                                      <div className="grid grid-cols-2 gap-y-2 text-xs">
                                        <div>
                                          <span className="text-slate-400">Payment Date:</span>
                                          <p className="font-medium">{formatDate(adv.finance_payment_date)}</p>
                                        </div>
                                        <div>
                                          <span className="text-slate-400">Amount Paid:</span>
                                          <p className="font-bold text-slate-800">{fmtINR(adv.finance_paid_amount)}</p>
                                        </div>
                                        <div>
                                          <span className="text-slate-400">Payment Mode:</span>
                                          <p className="font-medium">{adv.payment_mode || '-'}</p>
                                        </div>
                                        <div>
                                          <span className="text-slate-400">UTR / Ref No:</span>
                                          <p className="font-mono text-slate-600 truncate" title={adv.finance_ref}>{adv.finance_ref || '-'}</p>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-xs text-slate-500 py-4 text-center">No finance disbursement payments recorded.</div>
                                    )}
                                  </div>

                                  {/* Linked Claims */}
                                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-150">
                                    <h5 className="text-xs font-bold text-slate-600 mb-2 border-b pb-1 border-slate-200">Linked Expense Claims</h5>
                                    {linkedClaims.length > 0 ? (
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead>
                                            <tr className="text-slate-400 border-b border-slate-200 text-left">
                                              <th className="pb-1 font-semibold">Claim Ref</th>
                                              <th className="pb-1 text-right font-semibold">Submitted</th>
                                              <th className="pb-1 text-right font-semibold">Approved</th>
                                              <th className="pb-1 text-center font-semibold">Status</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {linkedClaims.map((claim, cIdx) => (
                                              <tr key={cIdx} className="border-b border-slate-100 last:border-0 text-slate-700">
                                                <td className="py-1 font-mono font-medium">{claim.sheet_number || String(claim._id).slice(-6)}</td>
                                                <td className="py-1 text-right">{fmtINR(claim.total_amount)}</td>
                                                <td className="py-1 text-right text-emerald-600">
                                                  {claim.status === 'approved' || claim.status === 'paid' || claim.status === 'settled' ? fmtINR(claim.total_amount) : '₹0.00'}
                                                </td>
                                                <td className="py-1 text-center">
                                                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                                    claim.status === 'approved' || claim.status === 'settled' ? 'bg-emerald-100 text-emerald-800' :
                                                    claim.status === 'rejected' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                                                  }`}>
                                                    {claim.status}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-xs text-slate-500 py-4 text-center">No linked claims submitted for this advance.</div>
                                    )}
                                  </div>
                                </div>

                                {/* Child Sub-Ledger Table */}
                                <div>
                                  <h4 className="text-[11px] font-bold uppercase text-slate-400 tracking-wider mb-2">Advance Ledger Details</h4>
                                  {processedRows.length === 0 ? (
                                    <div className="text-xs text-slate-500 text-center py-4">No ledger transaction entries recorded.</div>
                                  ) : (
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="py-2 h-auto text-xs">Date</TableHead>
                                          <TableHead className="py-2 h-auto text-xs">Ref No</TableHead>
                                          <TableHead className="py-2 h-auto text-xs">Type</TableHead>
                                          <TableHead className="py-2 h-auto text-xs">Particulars</TableHead>
                                          <TableHead className="py-2 h-auto text-xs text-right">Debit</TableHead>
                                          <TableHead className="py-2 h-auto text-xs text-right">Credit</TableHead>
                                          <TableHead className="py-2 h-auto text-xs text-right">Running Balance</TableHead>
                                          <TableHead className="py-2 h-auto text-xs">Remarks</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {processedRows.map((r, k) => (
                                          <TableRow key={k} className="hover:bg-slate-50/50">
                                            <TableCell className="py-2 text-xs">{formatDate(r.txn_date)}</TableCell>
                                            <TableCell className="py-2 font-mono text-xs">{r.ref_no}</TableCell>
                                            <TableCell className="py-2 text-xs">{TXN_LABEL[r.txn_type] || r.txn_type}</TableCell>
                                            <TableCell className="py-2 text-xs">
                                              {r.particulars}
                                              {r.txn_type === 'claim_adjustment' && r.claim_id && (
                                                <span className="text-[10px] text-slate-400 block">
                                                  Against Claim: {(summary.claims || []).find(c => String(c._id) === r.claim_id)?.sheet_number || r.claim_id.slice(-6).toUpperCase()}
                                                </span>
                                              )}
                                            </TableCell>
                                            <TableCell className="py-2 text-right text-xs">{r.debit > 0 ? fmtINR(r.debit) : '-'}</TableCell>
                                            <TableCell className="py-2 text-right text-xs text-emerald-600">{r.credit > 0 ? fmtINR(r.credit) : '-'}</TableCell>
                                            <TableCell className={`py-2 text-right font-medium text-xs ${
                                              r.running_balance_local > 0 ? 'text-amber-700' : 'text-emerald-700'
                                            }`}>
                                              {fmtINR(r.running_balance_local)}
                                            </TableCell>
                                            <TableCell className="py-2 text-slate-500 text-xs max-w-xs truncate" title={r.remarks}>{r.remarks || '-'}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </MainLayout>
  );
}
